import { toast } from '@/hooks/use-toast';
import { useBreakdownReferencesStore } from './useBreakdownReferencesStore';
import type { BreakdownReference } from '@/types/ai-session';
/**
 * UI 状态
 */
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useAppStore } from './appStore';
import { useEditorStore } from './editorStore';
import { invalidateOnProjectSwitch } from '@/lib/query-invalidation';
import {
  getAISessionStageSelection,
  isCompletedStoryboardStage,
  isProtectedTransientAISessionStage,
} from '@/lib/ai-session/workflow/active-stage';
import { parkAndRestoreEpisodeAISessionStage } from '@/lib/parse-flow-layout';
import type { DialoguePolishBusinessItem } from '@/lib/dialogue-utils';
import type { DialogueMentionItem } from '@/types/conversation-input';
import type { ParseFlowOrigin } from '@/types/parse-flow-file';
import type { PendingPreviewResourceMentionPayload } from '@/lib/conversations/preview-resource-mention';
import type { MentionToken } from '@/types/conversation-input';
import type { MentionResource } from '@/types/mention';
import type { AnnotationView, ProjectAnnotation } from '@/types/project-annotations';
import type { PreviewAnnotationTarget } from '@/lib/annotations/preview-annotation-target';
import type { PromptApplyMode } from '@/types/ai-conversation';

export interface PendingAnnotationLocate {
  annotation: ProjectAnnotation;
  routeProjectId: string;
  nonce: number;
}

export interface PendingTargetAnnotationOpen {
  annotationId: string;
  annotationView: AnnotationView;
  target: PreviewAnnotationTarget;
  nonce: number;
}

export type { DialoguePolishBusinessItem };

type LeftPanelTab = 'project' | 'library';
type RightPanelTab = 'imagePrompt' | 'angleTransform' | 'cameraAdjust';
type ResourcePanelTab = 'character' | 'location' | 'prop' | 'footage';
export type AnnotationPanelSurface = 'right-sidebar' | 'parse-flow' | 'canvas';
export type AISessionStageType = 'alignment' | 'asset_breakdown' | 'storyboard_breakdown';
export type AssetWorkspaceViewMode = 'directory-detail' | 'compact';

export interface ActiveAISessionStage {
  type: AISessionStageType;
  sessionId: string;
  stageStatus?: string | null;
  /** 仅用于派生展示与完成回执，活动选择不保存业务状态。 */
  stageRevision?: number | null;
  assetRunMode?: 'checklist' | 'detail' | null;
  skipHydration?: boolean;
}

export interface AISessionLaunchSelection {
  selectedSkillId: string | null;
  selectedSkillVersionId?: string | null;
  selectedModelName: string | null;
}

export type AISessionEditTargetStatus = 'selected' | 'running' | 'completed' | 'failed';

export interface ActiveAISessionEditTarget {
  reference?: BreakdownReference;
  mentionToken?: string;
  sessionId: string;
  kind: 'asset' | 'storyboard';
  label: string;
  status: AISessionEditTargetStatus;
  nonce: number;
  group?: string;
  name?: string;
  index?: number;
  /** 仅预填输入框，发送前不在卡片上显示「待修改」。 */
  quiet?: boolean;
}

export type AISessionChatPanelPlacementPreset = 'storyboard-panel' | 'left-of-storyboard-panel';

export interface AISessionChatPanelPlacement {
  preset?: AISessionChatPanelPlacementPreset;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  offset?: number;
}

export interface ActiveAISessionChatOptions {
  /** 模板应用回执关联；普通聊天请求会取消待处理预填。 */
  promptPrefillId?: number;
  preserveComposer?: boolean;
  panelPlacement?: AISessionChatPanelPlacement;
  stage?: ActiveAISessionStage;
  assistantHint?: string;
  editTarget?: Omit<ActiveAISessionEditTarget, 'status' | 'nonce'>;
  skipSessionHydration?: boolean;
  skipMessageSync?: boolean;
  autoSubmit?: boolean;
  autoStartStoryboardBreakdown?: boolean;
  /** 点分镜「拆分」后展开条数规格卡，选中才写入指令。 */
  openShotSplitPicker?: boolean;
}

export interface ActiveAISessionChatRequest {
  sessionId: string;
  nonce: number;
  initialText?: string;
  preserveComposer?: boolean;
  panelPlacement?: AISessionChatPanelPlacement;
  stage?: ActiveAISessionStage;
  assistantHint?: string;
  editTarget?: ActiveAISessionEditTarget;
  skipSessionHydration?: boolean;
  skipMessageSync?: boolean;
  autoSubmit?: boolean;
  autoStartStoryboardBreakdown?: boolean;
  openShotSplitPicker?: boolean;
}

export interface AISessionPromptPrefillInput {
  sessionId: string;
  content: string;
  options: ActiveAISessionChatOptions & {
    editTarget: Omit<ActiveAISessionEditTarget, 'status' | 'nonce'>;
  };
  /** 工作区校验对象身份、版本、权限和挂载状态，不持久化此回调。 */
  isValid: () => boolean;
}

export interface PendingAISessionPromptPrefill extends AISessionPromptPrefillInput {
  requestId: number;
  chatRequestNonce: number;
}

export interface PendingResourcePanelIntent {
  nonce: number;
  source: 'notification';
  notificationId: string;
  resourceKind: Exclude<ResourcePanelTab, 'footage'>;
  resourceName: string;
}

export interface ResourcePanelLocateSignal {
  nonce: number;
  resourceId: string;
  resourceType: ResourcePanelTab | null;
  tagId?: string | null;
}

export interface PendingDialoguePolishRequest {
  requestId: string;
  mode: 'single' | 'batch';
  dialogueId: string;
  dialogueIds: string[];
  prompt: string;
  dialogueReferences?: DialogueMentionItem[];
  /** 用于构建 businessParams.dialogues 的完整对白数据 */
  dialogues?: DialoguePolishBusinessItem[];
}

export interface PendingDialogueCreateRequest {
  requestId: string;
  storyboardId: string;
  content: string;
}

export interface PreviewInfoEditRequest {
  resourceId: string;
  nonce: number;
}

export interface PendingAIPromptApplyRequest {
  prompt: string;
  aiParams?: Record<string, unknown> | null;
  businessParams?: Record<string, unknown> | null;
  mode: PromptApplyMode;
}

export interface PendingReferenceMentionRequest {
  conversationKey: string;
  nonce: number;
  resource?: MentionResource;
  resources?: MentionResource[];
  replaceExisting?: boolean;
}

export interface PendingCanvasPromptApplyRequest extends PendingAIPromptApplyRequest {
  conversationKey: string;
  nonce: number;
}

interface UIState {
  // 侧边栏状态
  leftSidebarCollapsed: boolean;
  rightSidebarCollapsed: boolean;
  leftSidebarWidth: number;
  rightSidebarWidth: number;
  leftPanelActiveTab: LeftPanelTab;
  leftPanelTabSwitchSignal: { tab: LeftPanelTab; nonce: number } | null;
  rightPanelActiveTab: RightPanelTab;
  rightSidebarContent:
    | 'properties'
    | 'dialogue'
    | 'voice-generation'
    | 'sound-effect'
    | 'video'
    | 'storyboard-resources';

  // 资源面板浮层状态
  resourcesPanelOpen: boolean;
  resourcePanelActiveTab: ResourcePanelTab;
  pendingResourcePanelIntent: PendingResourcePanelIntent | null;
  resourcePanelLocateSignal: ResourcePanelLocateSignal | null;
  /** 单调递增，清空 signal 时不回绕，避免与面板 handled nonce 碰撞。 */
  resourcePanelLocateNonceSeq: number;
  /** 某分镜被用户手动关闭资源面板后，禁止自动再次打开 */
  resourcePanelAutoOpenSuppressedStoryboardId: string | null;

  // 分镜看板状态
  storyboardBoardExpanded: boolean;
  storyboardBoardHeight: number;

  // AI 面板
  assetWorkspaceViewMode: AssetWorkspaceViewMode;
  setAssetWorkspaceViewMode: (mode: AssetWorkspaceViewMode) => void;
  aiPanelVisible: boolean;
  aiSessionLaunchSelection: AISessionLaunchSelection;
  activeAISessionStage: ActiveAISessionStage | null;
  /** 按单集 projectId 挂起的 AI 工作区，切集后可原样恢复。 */
  episodeAISessionStages: Record<string, ActiveAISessionStage>;
  dismissedAISessionStage: ActiveAISessionStage | null;
  activeAISessionChatRequest: ActiveAISessionChatRequest | null;
  pendingAISessionPromptPrefill: PendingAISessionPromptPrefill | null;
  aiSessionPromptPrefillSeq: number;
  aiSessionChatNonceSeq: number;
  requestAISessionPromptPrefill: (input: AISessionPromptPrefillInput) => number | null;
  cancelAISessionPromptPrefill: (requestId: number) => void;
  activeAISessionEditTarget: ActiveAISessionEditTarget | null;

  // 项目面板状态
  projectPanelOpen: boolean;

  // 项目选择状态
  selectedProjectId: string | null;
  pendingProjectSwitchId: string | null;

  // 弹窗状态
  createProjectModalOpen: boolean;
  createProjectParentInfo: { id: string; name: string } | null;
  createProjectInitialValues: {
    projectType?: string;
    folderId?: string;
    parseFlowOrigin?: ParseFlowOrigin;
    parseFlowReturnTo?: string;
  } | null;
  editingProjectInfo: {
    id: string;
    name: string;
    folderId?: string;
    projectType?: string;
    aspectRatio?: string;
    summary?: string;
    synopsis?: string;
    description?: string;
    createdAt?: string;
    contentType?: 'ad' | 'movie' | 'series' | 'anime' | 'short' | 'concept';
    styleId?: string | null;
    visualStyle?: string | null;
    screenplayContent?: string;
    parentProjectId?: string | null;
    aiCreditsBudget?: number;
    readOnly?: boolean;
    memberConfigInheritance?: {
      enabled: boolean;
      sourceType: 'root_bootstrap' | 'subproject' | 'none';
      sourceProjectId?: string | null;
      sourceProjectName?: string | null;
    } | null;
  } | null;
  createFolderModalOpen: boolean;
  createFolderParentInfo: { id: string; name: string; parentProjectId?: string } | null; // 创建文件夹时的父文件夹信息
  editingFolderInfo: {
    id: string;
    name: string;
    description?: string;
    color?: string;
    parentProjectId?: string;
  } | null; // 编辑文件夹时的信息
  moveFolderModalOpen: boolean;
  movingFolderInfo: {
    id: string;
    name: string;
    parentId: string | null;
    folderCategory?: number;
    parentProjectId?: string;
  } | null; // 移动文件夹时的信息
  cloneFolderModalOpen: boolean;
  cloningFolderInfo: { id: string; name: string } | null; // 克隆文件夹时的信息
  deleteConfirmOpen: boolean;
  deletingResource: {
    type: 'folder' | 'project' | 'subproject';
    id: string;
    name: string;
    parentProjectId?: string;
    isEmpty?: boolean;
  } | null; // 删除资源时的信息
  settingsModalOpen: boolean;

  // 引导状态
  showOnboarding: boolean;

  // 待应用的 AI 提示词（跨组件通信：HistoryPanel → AIInputPanel）
  pendingAIPrompt: string | PendingAIPromptApplyRequest | null;
  setPendingAIPrompt: (prompt: string | PendingAIPromptApplyRequest | null) => void;
  pendingDialoguePolishRequest: PendingDialoguePolishRequest | null;
  setPendingDialoguePolishRequest: (request: PendingDialoguePolishRequest | null) => void;
  pendingDialogueCreateRequest: PendingDialogueCreateRequest | null;
  setPendingDialogueCreateRequest: (request: PendingDialogueCreateRequest | null) => void;
  previewInfoEditRequest: PreviewInfoEditRequest | null;
  requestPreviewInfoEdit: (resourceId: string) => void;
  clearPreviewInfoEditRequest: () => void;

  // 预览图片插入编辑器（跨组件通信：PreviewPanel → AIInputPanel）
  previewResourceMentionNonce: number;
  pendingPreviewResourceMention: (PendingPreviewResourceMentionPayload & { nonce: number }) | null;
  setPendingPreviewResourceMention: (payload: PendingPreviewResourceMentionPayload | null) => void;
  referenceMentionRequestNonce: number;
  pendingReferenceMentionRequest: (PendingReferenceMentionRequest & { nonce: number }) | null;
  setPendingReferenceMentionRequest: (request: PendingReferenceMentionRequest | null) => void;
  /** 与 referenceMention 同构：清除 pending 时不重置，避免同会话连续应用被 nonce 短路 */
  canvasPromptApplyRequestNonce: number;
  pendingCanvasPromptApplyRequest: PendingCanvasPromptApplyRequest | null;
  setPendingCanvasPromptApplyRequest: (
    request: Omit<PendingCanvasPromptApplyRequest, 'nonce'> | null
  ) => void;
  /** 画板生成框上方会话历史弹层：按 targetKey 持久，读取不消费。 */
  canvasConversationHistoryOverlayTargetKey: string | null;
  openCanvasConversationHistoryOverlay: (targetKey: string) => void;
  closeCanvasConversationHistoryOverlay: () => void;
  /** 画板当前会话覆盖：按 conversationContextKey，不 persist。 */
  canvasConversationOverrideByContextKey: Record<string, string>;
  setCanvasConversationOverride: (contextKey: string, conversationId: string) => void;

  // 配音卡片对白关联（跨组件通信：VoicePlayer → StoryboardPropertiesPanel → AIInputPanel）
  pendingVoiceDialogueReference: { item: DialogueMentionItem; nonce: number } | null;
  setPendingVoiceDialogueReference: (item: DialogueMentionItem | null) => void;

  /** 关闭预览详情/历史面板 signal（用于 AI 输入区触发：关闭面板以展示生成进度） */
  closePreviewInfoPanelSignal: number;
  requestClosePreviewInfoPanel: () => void;

  // 提及块内容预览浮层（跨组件通信：AIInputPanel → PreviewPanel）
  previewMentionResource: MentionToken | null;
  setPreviewMentionResource: (token: MentionToken | null) => void;

  // 操作
  toggleResourcesPanel: () => void;
  setResourcesPanelOpen: (open: boolean) => void;
  setResourcePanelActiveTab: (tab: ResourcePanelTab) => void;
  setPendingResourcePanelIntent: (intent: Omit<PendingResourcePanelIntent, 'nonce'> | null) => void;
  requestResourcePanelLocate: (
    resourceId: string,
    resourceType: ResourcePanelTab | null,
    tagId?: string | null
  ) => void;
  clearResourcePanelLocateSignal: () => void;
  setResourcePanelAutoOpenSuppressedStoryboardId: (storyboardId: string | null) => void;
  toggleLeftSidebar: () => void;
  toggleRightSidebar: () => void;
  setLeftSidebarWidth: (width: number) => void;
  setRightSidebarWidth: (width: number) => void;
  switchLeftPanelTab: (tab: LeftPanelTab) => void;
  setLeftPanelTab: (tab: LeftPanelTab) => void;
  setRightPanelTab: (tab: RightPanelTab) => void;
  toggleStoryboardBoard: () => void;
  setStoryboardBoardHeight: (height: number) => void;
  toggleAIPanel: () => void;
  setAIPanelVisible: (visible: boolean) => void;
  setAISessionLaunchSelection: (selection: Partial<AISessionLaunchSelection>) => void;
  setActiveAISessionStage: (stage: ActiveAISessionStage | null) => void;
  setActiveAISessionEditTarget: (target: ActiveAISessionEditTarget | null) => void;
  dismissAISessionWorkspace: (stage: ActiveAISessionStage | null) => void;
  exitAISessionWorkspace: (completedProjectId?: string) => void;
  switchAISessionWorkspaceForEpisode: (fromProjectId: string, toProjectId: string) => void;
  parkAISessionWorkspaceForEpisode: (projectId: string) => void;
  requestAISessionChat: (
    sessionId: string,
    initialText?: string,
    options?: ActiveAISessionChatOptions
  ) => number | null;
  setProjectPanelOpen: (open: boolean) => void;
  toggleProjectPanel: () => void;

  // 联动操作
  switchToLibraryWithProject: (projectId: string, projectName: string) => void;
  selectProject: (
    projectId: string | null,
    projectName?: string,
    options?: {
      autoCloseProjectPanel?: boolean;
      navigateToProject?: (projectId: string) => void;
    }
  ) => void;
  setPendingProjectSwitchId: (projectId: string | null) => void;

  setCreateProjectModalOpen: (
    open: boolean,
    parentInfo?: { id: string; name: string } | null,
    editingInfo?: UIState['editingProjectInfo'],
    initialValues?: {
      projectType?: string;
      folderId?: string;
      parseFlowOrigin?: ParseFlowOrigin;
      parseFlowReturnTo?: string;
    } | null
  ) => void;
  setCreateFolderModalOpen: (
    open: boolean,
    parentInfo?: { id: string; name: string; parentProjectId?: string } | null,
    editingInfo?: {
      id: string;
      name: string;
      description?: string;
      color?: string;
      parentProjectId?: string;
    } | null
  ) => void;
  setEditingFolderInfo: (
    info: {
      id: string;
      name: string;
      description?: string;
      color?: string;
      parentProjectId?: string;
    } | null
  ) => void;
  setMoveFolderModalOpen: (
    open: boolean,
    movingInfo?: {
      id: string;
      name: string;
      parentId: string | null;
      folderCategory?: number;
      parentProjectId?: string;
    } | null
  ) => void;
  setCloneFolderModalOpen: (
    open: boolean,
    cloningInfo?: { id: string; name: string } | null
  ) => void;
  setDeleteConfirmOpen: (
    open: boolean,
    resource?: {
      type: 'folder' | 'project' | 'subproject';
      id: string;
      name: string;
      parentProjectId?: string;
      isEmpty?: boolean;
    } | null
  ) => void;
  setSettingsModalOpen: (open: boolean) => void;
  setRightSidebarContent: (
    content:
      | 'properties'
      | 'dialogue'
      | 'voice-generation'
      | 'sound-effect'
      | 'video'
      | 'storyboard-resources'
  ) => void;
  setShowOnboarding: (show: boolean) => void;

  // AI 任务入口动画
  /** 每次提交生成任务时递增，TopBar 图标监听此值播放脉冲动画 */
  aiJobsSubmitSignal: number;
  /** 通知有新的生成任务被提交 */
  notifyAIJobSubmit: () => void;

  // 批注面板状态
  annotationPanelOpen: boolean;
  annotationPanelSurface: AnnotationPanelSurface | null;
  annotationActionableCount: number;
  openAnnotationPanel: (surface: AnnotationPanelSurface) => void;
  closeAnnotationPanel: () => void;
  toggleAnnotationPanel: (surface: AnnotationPanelSurface) => void;
  setAnnotationPanelOpen: (open: boolean) => void;
  setAnnotationActionableCount: (count: number) => void;
  pendingAnnotationLocate: PendingAnnotationLocate | null;
  queueAnnotationLocate: (annotation: ProjectAnnotation, routeProjectId: string) => void;
  clearPendingAnnotationLocate: () => void;
  pendingTargetAnnotationOpen: PendingTargetAnnotationOpen | null;
  queueTargetAnnotationOpen: (payload: Omit<PendingTargetAnnotationOpen, 'nonce'>) => void;
  clearPendingTargetAnnotationOpen: (nonce?: number) => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set, get) => ({
        // 初始状态
        resourcesPanelOpen: false, // 默认隐藏
        resourcePanelActiveTab: 'character', // 默认选中角色
        pendingResourcePanelIntent: null,
        resourcePanelLocateSignal: null,
        resourcePanelLocateNonceSeq: 0,
        resourcePanelAutoOpenSuppressedStoryboardId: null,
        leftSidebarCollapsed: false,
        rightSidebarCollapsed: false,
        leftSidebarWidth: 480,
        rightSidebarWidth: 480,
        leftPanelActiveTab: 'project',
        leftPanelTabSwitchSignal: null,
        rightPanelActiveTab: 'imagePrompt',
        rightSidebarContent: 'properties',
        storyboardBoardExpanded: true,
        storyboardBoardHeight: 300,
        aiPanelVisible: false,
        assetWorkspaceViewMode: 'directory-detail',
        setAssetWorkspaceViewMode: (mode) => set({ assetWorkspaceViewMode: mode }),
        aiSessionLaunchSelection: {
          selectedSkillId: null,
          selectedSkillVersionId: null,
          selectedModelName: null,
        },
        activeAISessionStage: null,
        episodeAISessionStages: {},
        dismissedAISessionStage: null,
        activeAISessionChatRequest: null,
        pendingAISessionPromptPrefill: null,
        aiSessionPromptPrefillSeq: 0,
        aiSessionChatNonceSeq: 0,
        activeAISessionEditTarget: null,
        projectPanelOpen: false,
        selectedProjectId: null,
        pendingProjectSwitchId: null,
        createProjectModalOpen: false,
        createProjectParentInfo: null,
        createProjectInitialValues: null,
        editingProjectInfo: null,
        createFolderModalOpen: false,
        createFolderParentInfo: null,
        editingFolderInfo: null,
        moveFolderModalOpen: false,
        movingFolderInfo: null,
        cloneFolderModalOpen: false,
        cloningFolderInfo: null,
        deleteConfirmOpen: false,
        deletingResource: null,
        settingsModalOpen: false,
        showOnboarding: true,
        pendingAIPrompt: null,
        pendingDialoguePolishRequest: null,
        pendingDialogueCreateRequest: null,
        previewInfoEditRequest: null,
        previewResourceMentionNonce: 0,
        pendingPreviewResourceMention: null,
        referenceMentionRequestNonce: 0,
        pendingReferenceMentionRequest: null,
        canvasPromptApplyRequestNonce: 0,
        pendingCanvasPromptApplyRequest: null,
        canvasConversationHistoryOverlayTargetKey: null,
        canvasConversationOverrideByContextKey: {},
        pendingVoiceDialogueReference: null,
        previewMentionResource: null,

        // 操作
        setPendingAIPrompt: (prompt) => set({ pendingAIPrompt: prompt }),
        setPendingDialoguePolishRequest: (request) =>
          set({ pendingDialoguePolishRequest: request }),
        setPendingDialogueCreateRequest: (request) =>
          set({ pendingDialogueCreateRequest: request }),
        requestPreviewInfoEdit: (resourceId) =>
          set((state) => ({
            previewInfoEditRequest: {
              resourceId,
              nonce:
                state.previewInfoEditRequest?.resourceId === resourceId
                  ? state.previewInfoEditRequest.nonce + 1
                  : 1,
            },
          })),
        clearPreviewInfoEditRequest: () => set({ previewInfoEditRequest: null }),
        setPendingPreviewResourceMention: (payload) =>
          set((state) => {
            if (!payload) return { pendingPreviewResourceMention: null };
            const nonce = state.previewResourceMentionNonce + 1;
            return {
              previewResourceMentionNonce: nonce,
              pendingPreviewResourceMention: {
                ...payload,
                nonce,
              },
            };
          }),
        setPendingReferenceMentionRequest: (request) =>
          set((state) => {
            if (!request) return { pendingReferenceMentionRequest: null };
            const nonce = state.referenceMentionRequestNonce + 1;
            return {
              referenceMentionRequestNonce: nonce,
              pendingReferenceMentionRequest: {
                ...request,
                nonce,
              },
            };
          }),
        setPendingCanvasPromptApplyRequest: (request) =>
          set((state) => {
            if (!request) return { pendingCanvasPromptApplyRequest: null };
            const nonce = state.canvasPromptApplyRequestNonce + 1;
            return {
              canvasPromptApplyRequestNonce: nonce,
              pendingCanvasPromptApplyRequest: {
                ...request,
                nonce,
              },
            };
          }),
        openCanvasConversationHistoryOverlay: (targetKey) =>
          set({ canvasConversationHistoryOverlayTargetKey: targetKey }),
        closeCanvasConversationHistoryOverlay: () =>
          set({ canvasConversationHistoryOverlayTargetKey: null }),
        setCanvasConversationOverride: (contextKey, conversationId) =>
          set((state) => {
            if (!contextKey || !conversationId) {
              return state;
            }
            return {
              canvasConversationOverrideByContextKey: {
                ...state.canvasConversationOverrideByContextKey,
                [contextKey]: conversationId,
              },
            };
          }),
        setPendingVoiceDialogueReference: (item) =>
          set((state) => ({
            pendingVoiceDialogueReference: item
              ? {
                  item,
                  nonce: (state.pendingVoiceDialogueReference?.nonce ?? 0) + 1,
                }
              : null,
          })),
        closePreviewInfoPanelSignal: 0,
        requestClosePreviewInfoPanel: () =>
          set((state) => ({ closePreviewInfoPanelSignal: state.closePreviewInfoPanelSignal + 1 })),
        setPreviewMentionResource: (token) => set({ previewMentionResource: token }),

        // AI 任务入口动画
        aiJobsSubmitSignal: 0,
        notifyAIJobSubmit: () =>
          set((state) => ({ aiJobsSubmitSignal: state.aiJobsSubmitSignal + 1 })),

        // 批注面板
        annotationPanelOpen: false,
        annotationPanelSurface: null,
        annotationActionableCount: 0,
        pendingAnnotationLocate: null,
        pendingTargetAnnotationOpen: null,
        openAnnotationPanel: (surface) =>
          set({
            annotationPanelOpen: true,
            annotationPanelSurface: surface,
            ...(surface === 'right-sidebar' ? { rightSidebarCollapsed: false } : {}),
          }),
        closeAnnotationPanel: () =>
          set({
            annotationPanelOpen: false,
            annotationPanelSurface: null,
          }),
        toggleAnnotationPanel: (surface) =>
          set((state) => {
            if (state.annotationPanelOpen && state.annotationPanelSurface === surface) {
              return {
                annotationPanelOpen: false,
                annotationPanelSurface: null,
              };
            }
            return {
              annotationPanelOpen: true,
              annotationPanelSurface: surface,
              ...(surface === 'right-sidebar' ? { rightSidebarCollapsed: false } : {}),
            };
          }),
        setAnnotationPanelOpen: (open) =>
          set((state) => ({
            annotationPanelOpen: open,
            annotationPanelSurface: open ? state.annotationPanelSurface : null,
          })),
        setAnnotationActionableCount: (count) => set({ annotationActionableCount: count }),
        queueAnnotationLocate: (annotation, routeProjectId) =>
          set((state) => ({
            pendingAnnotationLocate: {
              annotation,
              routeProjectId,
              nonce: (state.pendingAnnotationLocate?.nonce ?? 0) + 1,
            },
          })),
        clearPendingAnnotationLocate: () => set({ pendingAnnotationLocate: null }),
        queueTargetAnnotationOpen: (payload) =>
          set((state) => ({
            pendingTargetAnnotationOpen: {
              ...payload,
              nonce: (state.pendingTargetAnnotationOpen?.nonce ?? 0) + 1,
            },
          })),
        clearPendingTargetAnnotationOpen: (nonce) =>
          set((state) => {
            if (
              nonce !== undefined &&
              state.pendingTargetAnnotationOpen &&
              state.pendingTargetAnnotationOpen.nonce !== nonce
            ) {
              return state;
            }
            return { pendingTargetAnnotationOpen: null };
          }),
        toggleResourcesPanel: () =>
          set((state) => {
            const nextOpen = !state.resourcesPanelOpen;
            return {
              resourcesPanelOpen: nextOpen,
              leftPanelActiveTab: nextOpen ? 'library' : 'project',
            };
          }),
        setResourcesPanelOpen: (open: boolean) =>
          set({
            resourcesPanelOpen: open,
            leftPanelActiveTab: open ? 'library' : 'project',
          }),
        setResourcePanelActiveTab: (tab: ResourcePanelTab) => set({ resourcePanelActiveTab: tab }),
        setPendingResourcePanelIntent: (intent) =>
          set((state) => ({
            pendingResourcePanelIntent: intent
              ? {
                  ...intent,
                  nonce: (state.pendingResourcePanelIntent?.nonce ?? 0) + 1,
                }
              : null,
          })),
        requestResourcePanelLocate: (resourceId, resourceType, tagId = null) =>
          set((state) => {
            const nextNonce = state.resourcePanelLocateNonceSeq + 1;
            return {
              resourcePanelLocateNonceSeq: nextNonce,
              resourcePanelLocateSignal: {
                resourceId,
                resourceType,
                tagId,
                nonce: nextNonce,
              },
            };
          }),
        clearResourcePanelLocateSignal: () => set({ resourcePanelLocateSignal: null }),
        setResourcePanelAutoOpenSuppressedStoryboardId: (storyboardId: string | null) =>
          set({ resourcePanelAutoOpenSuppressedStoryboardId: storyboardId }),
        toggleLeftSidebar: () => set({ leftSidebarCollapsed: !get().leftSidebarCollapsed }),
        toggleRightSidebar: () => set({ rightSidebarCollapsed: !get().rightSidebarCollapsed }),
        setLeftSidebarWidth: (width) => set({ leftSidebarWidth: width }),
        setRightSidebarWidth: (width) => set({ rightSidebarWidth: width }),
        switchLeftPanelTab: (tab) =>
          set((state) => ({
            leftPanelActiveTab: tab,
            resourcesPanelOpen: tab === 'library',
            leftPanelTabSwitchSignal: {
              tab,
              nonce: (state.leftPanelTabSwitchSignal?.nonce ?? 0) + 1,
            },
          })),
        setLeftPanelTab: (tab) => get().switchLeftPanelTab(tab),
        setRightPanelTab: (tab) => set({ rightPanelActiveTab: tab }),
        toggleStoryboardBoard: () =>
          set({ storyboardBoardExpanded: !get().storyboardBoardExpanded }),
        setStoryboardBoardHeight: (height) => set({ storyboardBoardHeight: height }),
        toggleAIPanel: () => set({ aiPanelVisible: !get().aiPanelVisible }),
        setAIPanelVisible: (visible) =>
          set((state) => (state.aiPanelVisible === visible ? state : { aiPanelVisible: visible })),
        setAISessionLaunchSelection: (selection) =>
          set((state) => ({
            aiSessionLaunchSelection: {
              ...state.aiSessionLaunchSelection,
              ...selection,
            },
          })),
        setActiveAISessionStage: (stage) =>
          set((state) => {
            stage = getAISessionStageSelection(stage);
            const current = state.activeAISessionStage;
            if (
              current?.type === stage?.type &&
              current?.sessionId === stage?.sessionId &&
              current?.stageStatus === stage?.stageStatus &&
              current?.stageRevision === stage?.stageRevision &&
              current?.assetRunMode === stage?.assetRunMode &&
              current?.skipHydration === stage?.skipHydration
            ) {
              return state;
            }
            return {
              activeAISessionStage: stage,
              pendingAISessionPromptPrefill:
                stage?.sessionId === state.pendingAISessionPromptPrefill?.sessionId
                  ? state.pendingAISessionPromptPrefill
                  : null,
              dismissedAISessionStage: stage ? null : state.dismissedAISessionStage,
            };
          }),
        setActiveAISessionEditTarget: (target) => set({ activeAISessionEditTarget: target }),
        dismissAISessionWorkspace: (stage) =>
          set((state) => {
            // 采纳后等待资产/分镜拆解会话创建接口返回的短暂过渡占位态不应被清空，
            // 否则会话创建成功后也无法再写回全局状态，工作区将永久性丢失。
            if (isProtectedTransientAISessionStage(state.activeAISessionStage)) {
              return state;
            }
            return {
              aiPanelVisible: false,
              activeAISessionStage: null,
              dismissedAISessionStage: stage,
              activeAISessionChatRequest: null,
              pendingAISessionPromptPrefill: null,
              activeAISessionEditTarget: null,
            };
          }),
        exitAISessionWorkspace: (completedProjectId) =>
          set((state) => {
            if (isProtectedTransientAISessionStage(state.activeAISessionStage)) {
              return state;
            }
            const nextParked = { ...state.episodeAISessionStages };
            if (completedProjectId) delete nextParked[completedProjectId];
            return {
              aiPanelVisible: false,
              episodeAISessionStages: nextParked,
              activeAISessionStage: null,
              dismissedAISessionStage:
                completedProjectId && state.activeAISessionStage?.type === 'storyboard_breakdown'
                  ? { ...state.activeAISessionStage, stageStatus: 'confirmed' }
                  : (state.activeAISessionStage ?? state.dismissedAISessionStage),
              activeAISessionChatRequest: null,
              pendingAISessionPromptPrefill: null,
              activeAISessionEditTarget: null,
            };
          }),
        parkAISessionWorkspaceForEpisode: (projectId) =>
          set((state) => {
            if (!projectId) {
              return state;
            }
            if (isProtectedTransientAISessionStage(state.activeAISessionStage)) {
              return state;
            }
            const nextParked = { ...state.episodeAISessionStages };
            if (
              state.activeAISessionStage &&
              !isCompletedStoryboardStage(state.activeAISessionStage)
            ) {
              nextParked[projectId] = state.activeAISessionStage;
            } else {
              delete nextParked[projectId];
            }
            return {
              aiPanelVisible: false,
              episodeAISessionStages: nextParked,
              activeAISessionStage: null,
              dismissedAISessionStage: null,
              activeAISessionChatRequest: null,
              pendingAISessionPromptPrefill: null,
              activeAISessionEditTarget: null,
            };
          }),
        switchAISessionWorkspaceForEpisode: (fromProjectId, toProjectId) =>
          set((state) => {
            if (!fromProjectId || !toProjectId || fromProjectId === toProjectId) {
              return state;
            }
            if (isProtectedTransientAISessionStage(state.activeAISessionStage)) {
              return state;
            }
            const switched = parkAndRestoreEpisodeAISessionStage({
              parkedStages: state.episodeAISessionStages,
              fromProjectId,
              toProjectId,
              currentStage: state.activeAISessionStage,
            });
            return {
              episodeAISessionStages: switched.parkedStages,
              activeAISessionStage: getAISessionStageSelection(switched.activeStage),
              dismissedAISessionStage: null,
              activeAISessionChatRequest: null,
              pendingAISessionPromptPrefill: null,
              activeAISessionEditTarget: null,
              aiPanelVisible: switched.activeStage != null ? true : state.aiPanelVisible,
            };
          }),
        requestAISessionPromptPrefill: (input) => {
          if (!input.isValid() || !input.content.trim()) return null;
          const requestId = get().aiSessionPromptPrefillSeq + 1;
          // 准备阶段只打开会话，引用在用户确认后由原入口添加。
          const options = { ...input.options, editTarget: undefined };
          const chatRequestNonce = get().requestAISessionChat(input.sessionId, undefined, {
            ...options,
            preserveComposer: true,
            autoSubmit: false,
            skipSessionHydration: false,
            skipMessageSync: false,
          });
          if (chatRequestNonce === null) return null;
          set({
            aiSessionPromptPrefillSeq: requestId,
            pendingAISessionPromptPrefill: { ...input, requestId, chatRequestNonce },
          });
          return requestId;
        },
        cancelAISessionPromptPrefill: (requestId) =>
          set((state) =>
            state.pendingAISessionPromptPrefill?.requestId === requestId
              ? { pendingAISessionPromptPrefill: null }
              : state
          ),
        requestAISessionChat: (sessionId, initialText, options) => {
          let acceptedNonce: number | null = null;
          set((state) => {
            const reference = options?.editTarget?.reference;
            if (
              reference &&
              !useBreakdownReferencesStore.getState().add(reference, options.editTarget!.label)
            ) {
              toast({ title: '最多引用 12 处内容' });
              return state;
            }
            const referenceOnly = Boolean(
              reference && initialText?.trim() === options?.editTarget?.mentionToken?.trim()
            );
            const nonce =
              Math.max(state.aiSessionChatNonceSeq, state.activeAISessionChatRequest?.nonce ?? 0) +
              1;
            acceptedNonce = nonce;
            const editTarget = options?.editTarget
              ? {
                  ...options.editTarget,
                  status: 'selected' as const,
                  nonce,
                }
              : undefined;
            return {
              aiSessionChatNonceSeq: nonce,
              aiPanelVisible: true,
              activeAISessionStage: options?.stage
                ? getAISessionStageSelection(options.stage)
                : state.activeAISessionStage,
              dismissedAISessionStage: options?.stage ? null : state.dismissedAISessionStage,
              activeAISessionEditTarget: editTarget ?? state.activeAISessionEditTarget,
              pendingAISessionPromptPrefill:
                options?.promptPrefillId === state.pendingAISessionPromptPrefill?.requestId
                  ? state.pendingAISessionPromptPrefill
                  : null,
              activeAISessionChatRequest: {
                sessionId,
                nonce,
                initialText: referenceOnly ? undefined : initialText,
                preserveComposer: referenceOnly || options?.preserveComposer === true,
                panelPlacement: options?.panelPlacement,
                stage: options?.stage,
                assistantHint: options?.assistantHint,
                editTarget,
                skipSessionHydration: options?.skipSessionHydration,
                skipMessageSync: options?.skipMessageSync,
                autoSubmit: options?.autoSubmit,
                autoStartStoryboardBreakdown: options?.autoStartStoryboardBreakdown,
                openShotSplitPicker: options?.openShotSplitPicker,
              },
            };
          });
          return acceptedNonce;
        },
        setProjectPanelOpen: (open) => set({ projectPanelOpen: open }),
        toggleProjectPanel: () => set({ projectPanelOpen: !get().projectPanelOpen }),

        // 联动操作：切换到素材库并选择项目
        switchToLibraryWithProject: (projectId: string, projectName: string) => {
          set({ leftPanelActiveTab: 'library', resourcesPanelOpen: true });
          // 确保左侧边栏展开
          if (get().leftSidebarCollapsed) {
            set({ leftSidebarCollapsed: false });
          }
          // 触发项目选择事件（通过自定义事件）
          window.dispatchEvent(
            new CustomEvent('selectProjectInLibrary', {
              detail: { projectId, projectName },
            })
          );
        },
        setPendingProjectSwitchId: (projectId) => set({ pendingProjectSwitchId: projectId }),

        // 选择项目：统一的项目选择管理
        selectProject: (
          projectId: string | null,
          projectName?: string,
          options?: {
            autoCloseProjectPanel?: boolean;
            navigateToProject?: (projectId: string) => void;
          }
        ) => {
          const { autoCloseProjectPanel = true, navigateToProject } = options || {};

          // 更新 UI 状态
          const updates: Partial<UIState> = {
            selectedProjectId: projectId,
            pendingProjectSwitchId: projectId && navigateToProject ? projectId : null,
          };

          // 如果需要自动关闭项目面板，且选择了项目
          if (autoCloseProjectPanel && projectId) {
            updates.projectPanelOpen = false;
          }

          set(updates);

          // 更新应用状态
          useAppStore.getState().setCurrentProjectId(projectId);

          // 清理编辑器状态
          useEditorStore.getState().reset();

          // 失效新目标项目的查询缓存，确保拉取最新数据
          if (projectId) {
            invalidateOnProjectSwitch(projectId);
          }

          // 如果提供了导航函数，则导航到项目页面
          if (projectId && navigateToProject) {
            navigateToProject(projectId);
          }

          // 如果有项目名称，可以触发相关事件
          if (projectId && projectName) {
            window.dispatchEvent(
              new CustomEvent('projectSelected', {
                detail: { projectId, projectName },
              })
            );
          }
        },

        setCreateProjectModalOpen: (
          open,
          parentInfo = null,
          editingInfo = null,
          initialValues = null
        ) =>
          set({
            createProjectModalOpen: open,
            createProjectParentInfo: open ? parentInfo : null,
            editingProjectInfo: open ? editingInfo : null,
            createProjectInitialValues: open ? initialValues : null,
          }),
        setCreateFolderModalOpen: (open, parentInfo = null, editingInfo = null) =>
          set({
            createFolderModalOpen: open,
            createFolderParentInfo: open ? parentInfo : null,
            editingFolderInfo: open ? editingInfo : null,
          }),
        setEditingFolderInfo: (info) => set({ editingFolderInfo: info }),
        setMoveFolderModalOpen: (open, movingInfo = null) =>
          set({
            moveFolderModalOpen: open,
            movingFolderInfo: open ? movingInfo : null,
          }),
        setCloneFolderModalOpen: (open, cloningInfo = null) =>
          set({
            cloneFolderModalOpen: open,
            cloningFolderInfo: open ? cloningInfo : null,
          }),
        setDeleteConfirmOpen: (open, resource = null) =>
          set({
            deleteConfirmOpen: open,
            deletingResource: open ? resource : null,
          }),
        setSettingsModalOpen: (open) => set({ settingsModalOpen: open }),
        setRightSidebarContent: (content) => set({ rightSidebarContent: content }),
        setShowOnboarding: (show) => set({ showOnboarding: show }),
      }),
      {
        name: 'ui-store',
        merge: (persisted, current) => {
          const persistedState =
            persisted && typeof persisted === 'object' && !Array.isArray(persisted)
              ? (persisted as Partial<UIState>)
              : {};
          const leftPanelActiveTab =
            persistedState.leftPanelActiveTab ?? current.leftPanelActiveTab;

          return {
            ...current,
            assetWorkspaceViewMode:
              persistedState.assetWorkspaceViewMode === 'compact' ? 'compact' : 'directory-detail',
            leftSidebarCollapsed:
              persistedState.leftSidebarCollapsed ?? current.leftSidebarCollapsed,
            rightSidebarCollapsed:
              persistedState.rightSidebarCollapsed ?? current.rightSidebarCollapsed,
            leftSidebarWidth: persistedState.leftSidebarWidth ?? current.leftSidebarWidth,
            rightSidebarWidth: persistedState.rightSidebarWidth ?? current.rightSidebarWidth,
            leftPanelActiveTab,
            resourcePanelActiveTab:
              persistedState.resourcePanelActiveTab ?? current.resourcePanelActiveTab,
            storyboardBoardExpanded:
              persistedState.storyboardBoardExpanded ?? current.storyboardBoardExpanded,
            storyboardBoardHeight:
              persistedState.storyboardBoardHeight ?? current.storyboardBoardHeight,
            showOnboarding: persistedState.showOnboarding ?? current.showOnboarding,
            resourcesPanelOpen: leftPanelActiveTab === 'library',
            leftPanelTabSwitchSignal: null,
            projectPanelOpen: current.projectPanelOpen,
          };
        },
        onRehydrateStorage: () => (state) => {
          if (state?.leftPanelActiveTab === 'library' && !state.resourcesPanelOpen) {
            state.setResourcesPanelOpen(true);
          }
        },
        partialize: (state) => ({
          assetWorkspaceViewMode: state.assetWorkspaceViewMode,
          leftSidebarCollapsed: state.leftSidebarCollapsed,
          rightSidebarCollapsed: state.rightSidebarCollapsed,
          leftSidebarWidth: state.leftSidebarWidth,
          rightSidebarWidth: state.rightSidebarWidth,
          leftPanelActiveTab: state.leftPanelActiveTab,
          resourcePanelActiveTab: state.resourcePanelActiveTab,
          storyboardBoardExpanded: state.storyboardBoardExpanded,
          storyboardBoardHeight: state.storyboardBoardHeight,
          showOnboarding: state.showOnboarding,
        }),
      }
    ),
    { name: 'UIStore' }
  )
);
